import React, { useRef } from "react";
import * as XLSX from "xlsx";
import { generatePDFFromElement } from "../../../../src/utils/invoiceUtils";

const TeacherProfileReport = ({ 
  isOpen, 
  onClose, 
  selectedTeacherId,
  campusName,
  className,
  sectionName
}) => {
  const reportRef = useRef();

  // Dummy teacher profiles data based on teacher ID/name
  const getTeacherProfile = (teacherId) => {
    const teacherProfiles = {
      "TCH001": {
        id: "TCH001",
        name: "Dr. Sarah Khan",
        picture: "https://via.placeholder.com/150x150/4F46E5/FFFFFF?text=SK",
        qualification: "PhD in Mathematics, M.Ed, B.Sc Mathematics",
        subjectsTaught: ["Mathematics", "Statistics", "Advanced Mathematics"],
        gradesAssigned: ["Grade 8", "Grade 9", "Grade 10"],
        joiningDate: "01/09/2020",
        experience: "8 years",
      },
      "TCH002": {
        id: "TCH002",
        name: "Mr. Ali Hassan",
        picture: "https://via.placeholder.com/150x150/059669/FFFFFF?text=AH",
        qualification: "M.Sc Physics, B.Ed, B.Sc Physics",
        subjectsTaught: ["Physics", "Chemistry", "Advanced Physics"],
        gradesAssigned: ["Grade 9", "Grade 10", "Grade 11"],
        joiningDate: "15/08/2019",
        experience: "6 years",
      },
      "TCH003": {
        id: "TCH003",
        name: "Ms. Fatima Ahmed",
        picture: "https://via.placeholder.com/150x150/DC2626/FFFFFF?text=FA",
        qualification: "M.A English Literature, B.Ed, B.A English",
        subjectsTaught: ["English", "Literature", "Creative Writing"],
        gradesAssigned: ["Grade 6", "Grade 7", "Grade 8"],
        joiningDate: "01/09/2021",
        experience: "4 years",
      },
      "TCH004": {
        id: "TCH004",
        name: "Dr. Omar Sheikh",
        picture: "https://via.placeholder.com/150x150/7C3AED/FFFFFF?text=OS",
        qualification: "PhD in Biology, M.Ed, M.Sc Biology",
        subjectsTaught: ["Biology", "Environmental Science", "Advanced Biology"],
        gradesAssigned: ["Grade 10", "Grade 11", "Grade 12"],
        joiningDate: "01/09/2018",
        experience: "10 years",
      },
      "TCH005": {
        id: "TCH005",
        name: "Ms. Aisha Malik",
        picture: "https://via.placeholder.com/150x150/EA580C/FFFFFF?text=AM",
        qualification: "M.A Urdu, B.Ed, B.A Urdu",
        subjectsTaught: ["Urdu", "Islamic Studies", "Urdu Literature"],
        gradesAssigned: ["Grade 6", "Grade 7", "Grade 8"],
        joiningDate: "20/09/2020",
        experience: "5 years",
      }
    };

    // Return the specific teacher profile or default to TCH001
    return teacherProfiles[teacherId] || teacherProfiles["TCH001"];
  };

  const teacherProfile = getTeacherProfile(selectedTeacherId);

  // Dynamic data based on teacher profile
  const getTeacherData = (teacherId) => {
    const teacherData = {
      "TCH001": {
        attendance: {
          monthly: {
            "September 2023": { present: 22, absent: 3, total: 25, percentage: 88 },
            "October 2023": { present: 24, absent: 1, total: 25, percentage: 96 },
            "November 2023": { present: 23, absent: 2, total: 25, percentage: 92 },
            "December 2023": { present: 20, absent: 5, total: 25, percentage: 80 },
            "January 2024": { present: 24, absent: 1, total: 25, percentage: 96 },
            "February 2024": { present: 22, absent: 3, total: 25, percentage: 88 }
          },
          overall: { present: 135, absent: 15, total: 150, percentage: 90 }
        },
        salary: {
          monthlySalary: 85000,
          allowances: 15000,
          totalSalary: 100000,
          status: "Paid",
          paymentDate: "01/03/2024"
        }
      },
      "TCH002": {
        attendance: {
          monthly: {
            "September 2023": { present: 24, absent: 1, total: 25, percentage: 96 },
            "October 2023": { present: 23, absent: 2, total: 25, percentage: 92 },
            "November 2023": { present: 25, absent: 0, total: 25, percentage: 100 },
            "December 2023": { present: 22, absent: 3, total: 25, percentage: 88 },
            "January 2024": { present: 24, absent: 1, total: 25, percentage: 96 },
            "February 2024": { present: 23, absent: 2, total: 25, percentage: 92 }
          },
          overall: { present: 141, absent: 9, total: 150, percentage: 94 }
        },
        salary: {
          monthlySalary: 75000,
          allowances: 12000,
          totalSalary: 87000,
          status: "Paid",
          paymentDate: "01/03/2024"
        }
      },
      "TCH003": {
        attendance: {
          monthly: {
            "September 2023": { present: 21, absent: 4, total: 25, percentage: 84 },
            "October 2023": { present: 23, absent: 2, total: 25, percentage: 92 },
            "November 2023": { present: 22, absent: 3, total: 25, percentage: 88 },
            "December 2023": { present: 24, absent: 1, total: 25, percentage: 96 },
            "January 2024": { present: 23, absent: 2, total: 25, percentage: 92 },
            "February 2024": { present: 21, absent: 4, total: 25, percentage: 84 }
          },
          overall: { present: 134, absent: 16, total: 150, percentage: 89 }
        },
        salary: {
          monthlySalary: 65000,
          allowances: 10000,
          totalSalary: 75000,
          status: "Paid",
          paymentDate: "01/03/2024"
        }
      },
      "TCH004": {
        attendance: {
          monthly: {
            "September 2023": { present: 25, absent: 0, total: 25, percentage: 100 },
            "October 2023": { present: 24, absent: 1, total: 25, percentage: 96 },
            "November 2023": { present: 25, absent: 0, total: 25, percentage: 100 },
            "December 2023": { present: 23, absent: 2, total: 25, percentage: 92 },
            "January 2024": { present: 25, absent: 0, total: 25, percentage: 100 },
            "February 2024": { present: 24, absent: 1, total: 25, percentage: 96 }
          },
          overall: { present: 146, absent: 4, total: 150, percentage: 97 }
        },
        salary: {
          monthlySalary: 95000,
          allowances: 18000,
          totalSalary: 113000,
          status: "Paid",
          paymentDate: "01/03/2024"
        }
      },
      "TCH005": {
        attendance: {
          monthly: {
            "September 2023": { present: 22, absent: 3, total: 25, percentage: 88 },
            "October 2023": { present: 24, absent: 1, total: 25, percentage: 96 },
            "November 2023": { present: 23, absent: 2, total: 25, percentage: 92 },
            "December 2023": { present: 21, absent: 4, total: 25, percentage: 84 },
            "January 2024": { present: 24, absent: 1, total: 25, percentage: 96 },
            "February 2024": { present: 22, absent: 3, total: 25, percentage: 88 }
          },
          overall: { present: 136, absent: 14, total: 150, percentage: 91 }
        },
        salary: {
          monthlySalary: 70000,
          allowances: 11000,
          totalSalary: 81000,
          status: "Paid",
          paymentDate: "01/03/2024"
        }
      }
    };

    return teacherData[teacherId] || teacherData["TCH001"];
  };

  const teacherData = getTeacherData(selectedTeacherId);

  // Academic Years Performance - Dynamic based on teacher
  const getAcademicYears = (teacherId) => {
    const academicData = {
      "TCH001": [
        {
          year: "2023-24",
          terms: [
            {
              term: "Term 1",
              subjects: [
                { subject: "Mathematics Grade 8", averageGrade: "A+", averageMarks: 92, totalStudents: 35 },
                { subject: "Mathematics Grade 9", averageGrade: "A", averageMarks: 88, totalStudents: 32 },
                { subject: "Statistics Grade 10", averageGrade: "A+", averageMarks: 94, totalStudents: 28 }
              ],
              overallGPA: 3.8,
              overallPercentage: 91.3
            },
            {
              term: "Term 2",
              subjects: [
                { subject: "Mathematics Grade 8", averageGrade: "A+", averageMarks: 95, totalStudents: 35 },
                { subject: "Mathematics Grade 9", averageGrade: "A+", averageMarks: 91, totalStudents: 32 },
                { subject: "Statistics Grade 10", averageGrade: "A+", averageMarks: 96, totalStudents: 28 }
              ],
              overallGPA: 3.9,
              overallPercentage: 94.0
            }
          ],
          overallGPA: 3.85,
          overallPercentage: 92.7
        }
      ],
      "TCH002": [
        {
          year: "2023-24",
          terms: [
            {
              term: "Term 1",
              subjects: [
                { subject: "Physics Grade 9", averageGrade: "A", averageMarks: 89, totalStudents: 30 },
                { subject: "Physics Grade 10", averageGrade: "A+", averageMarks: 93, totalStudents: 28 },
                { subject: "Chemistry Grade 11", averageGrade: "A", averageMarks: 87, totalStudents: 25 }
              ],
              overallGPA: 3.7,
              overallPercentage: 89.7
            },
            {
              term: "Term 2",
              subjects: [
                { subject: "Physics Grade 9", averageGrade: "A+", averageMarks: 94, totalStudents: 30 },
                { subject: "Physics Grade 10", averageGrade: "A+", averageMarks: 96, totalStudents: 28 },
                { subject: "Chemistry Grade 11", averageGrade: "A+", averageMarks: 91, totalStudents: 25 }
              ],
              overallGPA: 3.9,
              overallPercentage: 93.7
            }
          ],
          overallGPA: 3.8,
          overallPercentage: 91.7
        }
      ],
      "TCH003": [
        {
          year: "2023-24",
          terms: [
            {
              term: "Term 1",
              subjects: [
                { subject: "English Grade 6", averageGrade: "A", averageMarks: 86, totalStudents: 32 },
                { subject: "English Grade 7", averageGrade: "B+", averageMarks: 83, totalStudents: 30 },
                { subject: "Literature Grade 8", averageGrade: "A", averageMarks: 88, totalStudents: 28 }
              ],
              overallGPA: 3.4,
              overallPercentage: 85.7
            },
            {
              term: "Term 2",
              subjects: [
                { subject: "English Grade 6", averageGrade: "A+", averageMarks: 92, totalStudents: 32 },
                { subject: "English Grade 7", averageGrade: "A", averageMarks: 87, totalStudents: 30 },
                { subject: "Literature Grade 8", averageGrade: "A+", averageMarks: 94, totalStudents: 28 }
              ],
              overallGPA: 3.8,
              overallPercentage: 91.0
            }
          ],
          overallGPA: 3.6,
          overallPercentage: 88.4
        }
      ],
      "TCH004": [
        {
          year: "2023-24",
          terms: [
            {
              term: "Term 1",
              subjects: [
                { subject: "Biology Grade 10", averageGrade: "A+", averageMarks: 95, totalStudents: 26 },
                { subject: "Biology Grade 11", averageGrade: "A+", averageMarks: 93, totalStudents: 24 },
                { subject: "Environmental Science Grade 12", averageGrade: "A", averageMarks: 89, totalStudents: 22 }
              ],
              overallGPA: 3.9,
              overallPercentage: 92.3
            },
            {
              term: "Term 2",
              subjects: [
                { subject: "Biology Grade 10", averageGrade: "A+", averageMarks: 97, totalStudents: 26 },
                { subject: "Biology Grade 11", averageGrade: "A+", averageMarks: 95, totalStudents: 24 },
                { subject: "Environmental Science Grade 12", averageGrade: "A+", averageMarks: 92, totalStudents: 22 }
              ],
              overallGPA: 4.0,
              overallPercentage: 94.7
            }
          ],
          overallGPA: 3.95,
          overallPercentage: 93.5
        }
      ],
      "TCH005": [
        {
          year: "2023-24",
          terms: [
            {
              term: "Term 1",
              subjects: [
                { subject: "Urdu Grade 6", averageGrade: "A", averageMarks: 87, totalStudents: 33 },
                { subject: "Urdu Grade 7", averageGrade: "B+", averageMarks: 84, totalStudents: 31 },
                { subject: "Islamic Studies Grade 8", averageGrade: "A", averageMarks: 89, totalStudents: 29 }
              ],
              overallGPA: 3.5,
              overallPercentage: 86.7
            },
            {
              term: "Term 2",
              subjects: [
                { subject: "Urdu Grade 6", averageGrade: "A+", averageMarks: 91, totalStudents: 33 },
                { subject: "Urdu Grade 7", averageGrade: "A", averageMarks: 86, totalStudents: 31 },
                { subject: "Islamic Studies Grade 8", averageGrade: "A+", averageMarks: 93, totalStudents: 29 }
              ],
              overallGPA: 3.8,
              overallPercentage: 90.0
            }
          ],
          overallGPA: 3.65,
          overallPercentage: 88.4
        }
      ]
    };

    return academicData[teacherId] || academicData["TCH001"];
  };

  const academicYears = getAcademicYears(selectedTeacherId);

  // Parent Feedback - Dynamic based on teacher
  const getParentFeedback = (teacherId) => {
    const feedbackData = {
      "TCH001": [
        {
          date: "15/12/2023",
          parentName: "Mr. Ahmed Hassan",
          studentName: "Ali Hassan",
          grade: "Grade 9",
          rating: 5,
          feedback: "Excellent teacher! My son's mathematics has improved significantly. Very patient and explains concepts clearly."
        },
        {
          date: "20/01/2024",
          parentName: "Ms. Fatima Khan",
          studentName: "Omar Khan",
          grade: "Grade 8",
          rating: 5,
          feedback: "Dr. Sarah is an outstanding teacher. She makes mathematics fun and engaging. Highly recommended!"
        },
        {
          date: "10/02/2024",
          parentName: "Mr. Hassan Ali",
          studentName: "Aisha Ali",
          grade: "Grade 10",
          rating: 4,
          feedback: "Very knowledgeable teacher. Sometimes moves too fast for some students, but overall excellent."
        }
      ],
      "TCH002": [
        {
          date: "18/12/2023",
          parentName: "Mr. Usman Sheikh",
          studentName: "Hassan Sheikh",
          grade: "Grade 10",
          rating: 5,
          feedback: "Mr. Ali is an amazing physics teacher. My son now loves physics and understands complex concepts easily."
        },
        {
          date: "25/01/2024",
          parentName: "Ms. Aisha Raza",
          studentName: "Omar Raza",
          grade: "Grade 11",
          rating: 5,
          feedback: "Excellent chemistry teacher! Very clear explanations and great lab demonstrations."
        }
      ],
      "TCH003": [
        {
          date: "12/12/2023",
          parentName: "Mr. Khalid Ahmed",
          studentName: "Zainab Ahmed",
          grade: "Grade 7",
          rating: 5,
          feedback: "Ms. Fatima has improved my daughter's English significantly. Very creative teaching methods."
        },
        {
          date: "28/01/2024",
          parentName: "Ms. Maryam Khan",
          studentName: "Ahmed Khan",
          grade: "Grade 8",
          rating: 4,
          feedback: "Good English teacher. Students enjoy her literature classes very much."
        }
      ],
      "TCH004": [
        {
          date: "20/12/2023",
          parentName: "Mr. Nadeem Ali",
          studentName: "Sara Ali",
          grade: "Grade 11",
          rating: 5,
          feedback: "Dr. Omar is the best biology teacher! My daughter's grades have improved dramatically."
        },
        {
          date: "15/02/2024",
          parentName: "Ms. Farah Sheikh",
          studentName: "Hassan Sheikh",
          grade: "Grade 12",
          rating: 5,
          feedback: "Outstanding teacher! Makes biology interesting and easy to understand."
        }
      ],
      "TCH005": [
        {
          date: "22/12/2023",
          parentName: "Mr. Tariq Malik",
          studentName: "Aisha Malik",
          grade: "Grade 7",
          rating: 5,
          feedback: "Ms. Aisha is an excellent Urdu teacher. My daughter's Urdu has improved a lot."
        },
        {
          date: "18/02/2024",
          parentName: "Ms. Noreen Ahmed",
          studentName: "Usman Ahmed",
          grade: "Grade 8",
          rating: 4,
          feedback: "Good Islamic Studies teacher. Students learn about Islamic values effectively."
        }
      ]
    };

    return feedbackData[teacherId] || feedbackData["TCH001"];
  };

  const parentFeedback = getParentFeedback(selectedTeacherId);

  // AI Recommendations - Dynamic based on teacher
  const getAIRecommendations = (teacherId) => {
    const recommendations = {
      "TCH001": [
        "Continue excellent performance in mathematics instruction",
        "Consider slowing down pace slightly for students who need more time to grasp concepts",
        "Maintain high attendance record and punctuality",
        "Explore interactive teaching methods to further engage students",
        "Consider mentoring junior mathematics teachers",
        "Continue professional development in advanced mathematics topics"
      ],
      "TCH002": [
        "Maintain excellent physics and chemistry instruction",
        "Continue with hands-on laboratory demonstrations",
        "Consider advanced physics workshops for students",
        "Keep up the great attendance record",
        "Explore more interactive science experiments",
        "Mentor new science teachers in the department"
      ],
      "TCH003": [
        "Continue improving English language skills of students",
        "Explore more creative writing activities",
        "Maintain good attendance and punctuality",
        "Consider literature appreciation workshops",
        "Use more multimedia resources in teaching",
        "Focus on improving students' speaking skills"
      ],
      "TCH004": [
        "Continue outstanding biology instruction",
        "Maintain perfect attendance record",
        "Explore more field trips for environmental science",
        "Consider advanced biology research projects",
        "Mentor junior biology teachers",
        "Continue professional development in biotechnology"
      ],
      "TCH005": [
        "Continue excellent Urdu and Islamic Studies instruction",
        "Focus on improving students' Urdu writing skills",
        "Maintain good attendance record",
        "Explore more Islamic history and culture activities",
        "Use more visual aids in teaching",
        "Consider organizing Urdu poetry competitions"
      ]
    };

    return recommendations[teacherId] || recommendations["TCH001"];
  };

  const aiRecommendations = getAIRecommendations(selectedTeacherId);

  const downloadPDF = () => {
    generatePDFFromElement(reportRef, "Teacher_Profile_Report.pdf", 2);
  };

  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ["Teacher Profile Report Summary"],
      ["Teacher ID", teacherProfile.id],
      ["Name", teacherProfile.name],
      ["Campus", campusName],
      ["Report Date", new Date().toLocaleDateString('en-GB')],
      [],
      ["Basic Information"],
      ["Qualification", teacherProfile.qualification],
      ["Subjects Taught", teacherProfile.subjectsTaught.join(", ")],
      ["Grades Assigned", teacherProfile.gradesAssigned.join(", ")],
      ["Joining Date", teacherProfile.joiningDate],
      ["Experience", teacherProfile.experience],
      [],
      ["Salary Information"],
      ["Monthly Salary", teacherData.salary.monthlySalary],
      ["Allowances", teacherData.salary.allowances],
      ["Total Salary", teacherData.salary.totalSalary],
      ["Payment Status", teacherData.salary.status],
      [],
      ["Overall Attendance"],
      ["Present Days", teacherData.attendance.overall.present],
      ["Absent Days", teacherData.attendance.overall.absent],
      ["Total Days", teacherData.attendance.overall.total],
      ["Attendance Percentage", teacherData.attendance.overall.percentage + "%"]
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // Academic Performance sheet
    const academicData = [
      ["Academic Performance"],
      ["Academic Year", "Term", "Subject", "Average Grade", "Average Marks", "Total Students"]
    ];

    academicYears.forEach(year => {
      year.terms.forEach(term => {
        term.subjects.forEach(subject => {
          academicData.push([
            year.year,
            term.term,
            subject.subject,
            subject.averageGrade,
            subject.averageMarks,
            subject.totalStudents
          ]);
        });
      });
    });

    const academicSheet = XLSX.utils.aoa_to_sheet(academicData);
    XLSX.utils.book_append_sheet(workbook, academicSheet, "Academic Performance");

    // Parent Feedback sheet
    const feedbackData = [
      ["Parent Feedback"],
      ["Date", "Parent Name", "Student Name", "Grade", "Rating", "Feedback"]
    ];

    parentFeedback.forEach(feedback => {
      feedbackData.push([
        feedback.date,
        feedback.parentName,
        feedback.studentName,
        feedback.grade,
        feedback.rating,
        feedback.feedback
      ]);
    });

    const feedbackSheet = XLSX.utils.aoa_to_sheet(feedbackData);
    XLSX.utils.book_append_sheet(workbook, feedbackSheet, "Parent Feedback");

    XLSX.writeFile(workbook, "Teacher_Profile_Report.xlsx");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[95%] max-h-[95vh] overflow-y-auto relative border border-gray-300">
        
        {/* Teacher Profile Report - One Page Per Teacher */}
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6 border-b pb-4">
            <h1 className="text-2xl font-bold text-[#241763]">{campusName}</h1>
            <h2 className="text-xl font-semibold mt-2">Teacher Profile Report</h2>
            <p className="text-base text-gray-600">Academic Year 2023-24</p>
          </div>

          {/* Teacher Photo and Basic Info */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="text-center">
              <div className="w-32 h-32 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-4xl font-bold text-gray-600">
                  {teacherProfile.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <p className="text-sm text-gray-600">Teacher Photo</p>
            </div>
            <div className="col-span-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex">
                  <p className="w-24 font-semibold">Teacher ID:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{teacherProfile.id}</p>
                </div>
                <div className="flex">
                  <p className="w-24 font-semibold">Name:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{teacherProfile.name}</p>
                </div>
                <div className="flex">
                  <p className="w-24 font-semibold">Qualification:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{teacherProfile.qualification}</p>
                </div>
                <div className="flex">
                  <p className="w-24 font-semibold">Experience:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{teacherProfile.experience}</p>
                </div>
                <div className="flex">
                  <p className="w-24 font-semibold">Joining Date:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{teacherProfile.joiningDate}</p>
                </div>
                <div className="flex">
                  <p className="w-24 font-semibold">Subjects:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{teacherProfile.subjectsTaught.join(", ")}</p>
                </div>
                <div className="flex col-span-2">
                  <p className="w-24 font-semibold">Grades:</p>
                  <p className="flex-1 border-b border-gray-400 ml-2">{teacherProfile.gradesAssigned.join(", ")}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Attendance Status */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-[#241763] border-b pb-2">Attendance Status</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-2">Monthly Attendance (2023-24)</h4>
                <div className="space-y-2">
                  {Object.entries(teacherData.attendance.monthly).map(([month, data]) => (
                    <div key={month} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span className="text-sm font-medium">{month}</span>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm">{data.present}/{data.total}</span>
                        <span className={`text-sm font-semibold ${
                          data.percentage >= 90 ? 'text-green-600' : 
                          data.percentage >= 80 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {data.percentage}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Overall Attendance</h4>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {teacherData.attendance.overall.percentage}%
                    </div>
                    <div className="text-sm text-gray-600">
                      {teacherData.attendance.overall.present} Present / {teacherData.attendance.overall.total} Total Days
                    </div>
                    <div className="text-sm text-gray-600">
                      {teacherData.attendance.overall.absent} Absent Days
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Salary Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-[#241763] border-b pb-2">Salary Information</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-green-50 p-4 rounded-lg text-center">
                <div className="text-sm text-gray-600">Monthly Salary</div>
                <div className="text-lg font-bold text-green-600">$. {teacherData.salary.monthlySalary.toLocaleString()}</div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <div className="text-sm text-gray-600">Allowances</div>
                <div className="text-lg font-bold text-blue-600">$. {teacherData.salary.allowances.toLocaleString()}</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg text-center">
                <div className="text-sm text-gray-600">Total Salary</div>
                <div className="text-lg font-bold text-purple-600">$. {teacherData.salary.totalSalary.toLocaleString()}</div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg text-center">
                <div className="text-sm text-gray-600">Status</div>
                <div className="text-lg font-bold text-orange-600">{teacherData.salary.status}</div>
              </div>
            </div>
          </div>

          {/* Academic Performance */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-[#241763] border-b pb-2">Academic Performance</h3>
            {academicYears.map((year, yearIndex) => (
              <div key={year.year} className="mb-4">
                <h4 className="font-semibold text-lg mb-3 text-gray-700">{year.year}</h4>
                {year.terms.map((term, termIndex) => (
                  <div key={term.term} className="mb-4 bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <h5 className="font-semibold">{term.term}</h5>
                      <div className="flex space-x-4">
                        <span className="text-sm">GPA: <strong>{term.overallGPA}</strong></span>
                        <span className="text-sm">Percentage: <strong>{term.overallPercentage}%</strong></span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {term.subjects.map((subject, subjectIndex) => (
                        <div key={subjectIndex} className="bg-white p-3 rounded text-center">
                          <div className="font-medium text-sm">{subject.subject}</div>
                          <div className="text-lg font-bold text-blue-600">{subject.averageGrade}</div>
                          <div className="text-xs text-gray-600">{subject.averageMarks}% ({subject.totalStudents} students)</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="bg-blue-100 p-3 rounded-lg text-center">
                  <div className="font-semibold">Overall {year.year}: GPA {year.overallGPA} | Percentage {year.overallPercentage}%</div>
                </div>
              </div>
            ))}
          </div>

          {/* Parent Feedback */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-[#241763] border-b pb-2">Parent Feedback</h3>
            <div className="space-y-3">
              {parentFeedback.map((feedback, index) => (
                <div key={index} className="bg-green-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium text-sm">{feedback.parentName} - {feedback.studentName} ({feedback.grade})</div>
                      <div className="text-xs text-gray-500">{feedback.date}</div>
                    </div>
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className={`text-lg ${i < feedback.rating ? 'text-yellow-400' : 'text-gray-300'}`}>★</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-sm text-gray-700">{feedback.feedback}</div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Recommendations */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-[#241763] border-b pb-2">AI-Based Recommendations for Improvement</h3>
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg">
              <ul className="space-y-2">
                {aiRecommendations.map((recommendation, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-purple-600 mr-2">•</span>
                    <span className="text-sm">{recommendation}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Signature Lines */}
          <div className="grid grid-cols-3 gap-8 mt-8 pt-4 border-t">
            <div className="text-center">
              <div className="border-t border-gray-400 mt-8 pt-2">
                <div className="font-semibold">Teacher</div>
                <div className="text-sm text-gray-600">Signature</div>
              </div>
            </div>
            <div className="text-center">
              <div className="border-t border-gray-400 mt-8 pt-2">
                <div className="font-semibold">Headmistress</div>
                <div className="text-sm text-gray-600">Signature</div>
              </div>
            </div>
            <div className="text-center">
              <div className="border-t border-gray-400 mt-8 pt-2">
                <div className="font-semibold">Principal</div>
                <div className="text-sm text-gray-600">Signature</div>
              </div>
            </div>
          </div>
        </div>

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-6 space-x-4">
          <button 
            onClick={downloadPDF} 
            className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded text-white transition-colors"
          >
            Download as PDF
          </button>
          <button 
            onClick={downloadExcel} 
            className="bg-green-500 hover:bg-green-600 px-4 py-2 rounded text-white transition-colors"
          >
            Download as Excel
          </button>
          <button 
            onClick={onClose} 
            className="bg-gray-400 hover:bg-gray-500 px-4 py-2 rounded text-white transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherProfileReport;
